/**
 * Atlas — Admin approval decision page (ops only).
 *
 * Reached via the link in send_admin_approval_request_email(). The email
 * sends users here with ?action=approve|reject&token=... and the page
 * REQUIRES a deliberate click before POSTing to the backend, so that
 * email link-prefetchers (Outlook Safe Links, Gmail bot, etc.) cannot
 * silently approve or reject an admin request.
 *
 * See SECURITY_AUDIT C-4 — GET was the original vulnerability; this page
 * exists so the email link still leads somewhere useful while requiring
 * an authenticated human click for the actual state change.
 */
import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';

function extractError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || 'Could not complete the action.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail[0]?.msg || 'Invalid request.';
  }
  return 'Could not complete the action.';
}

export default function AdminDecidePage() {
  const [params] = useSearchParams();
  const action = params.get('action');
  const token = params.get('token');

  const [confirming, setConfirming] = useState(false);
  const [state, setState] = useState({ status: 'idle', message: '' });

  const isApprove = action === 'approve';
  const isReject = action === 'reject';
  const validAction = isApprove || isReject;

  const submit = async () => {
    if (!token || !validAction) return;
    setConfirming(true);
    setState({ status: 'pending', message: '' });
    try {
      const res = isApprove
        ? await authAPI.adminApprove(token)
        : await authAPI.adminReject(token);
      setState({
        status: 'success',
        message: res?.data?.message || (isApprove ? 'Request approved.' : 'Request rejected.'),
      });
    } catch (err) {
      setState({ status: 'error', message: extractError(err) });
    } finally {
      setConfirming(false);
    }
  };

  // Bad URL
  if (!validAction || !token) {
    return (
      <Shell>
        <div className="panel" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--crimson)', color: 'var(--paper)', display: 'grid', placeItems: 'center', fontSize: 28, margin: '0 auto 20px' }}>✕</div>
          <div style={{ fontFamily: 'var(--serif)', fontSize: 22, fontWeight: 700, color: 'var(--crimson)', marginBottom: 12 }}>
            Invalid link
          </div>
          <p style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.7, marginBottom: 24 }}>
            The link you used is missing required parameters. Please use the most recent email from
            PropAtlas ops.
          </p>
          <Link to="/login" className="btn" style={{ textDecoration: 'none' }}>← Back to login</Link>
        </div>
      </Shell>
    );
  }

  if (state.status === 'success') {
    return (
      <Shell>
        <div className="panel" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: isApprove ? 'var(--forest)' : 'var(--ink-4)', color: 'var(--paper)', display: 'grid', placeItems: 'center', fontSize: 28, margin: '0 auto 20px' }}>
            {isApprove ? '✓' : '✕'}
          </div>
          <div style={{ fontFamily: 'var(--serif)', fontSize: 22, fontWeight: 700, color: isApprove ? 'var(--forest)' : 'var(--ink-2)', marginBottom: 12 }}>
            {isApprove ? 'Request approved' : 'Request rejected'}
          </div>
          <p style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.7, marginBottom: 24 }}>
            {state.message}
          </p>
          <Link to="/login" className="btn" style={{ textDecoration: 'none' }}>← Back to login</Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="panel" style={{ padding: 32 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{
            display: 'inline-block', padding: '6px 14px', borderRadius: 999,
            background: isApprove ? 'rgba(46,139,87,0.1)' : 'rgba(139,46,46,0.1)',
            color: isApprove ? 'var(--forest)' : 'var(--crimson)',
            fontSize: 12, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase',
            marginBottom: 16,
          }}>
            {isApprove ? '✓ Approve admin request' : '✕ Reject admin request'}
          </div>
          <div style={{ fontFamily: 'var(--serif)', fontSize: 22, fontWeight: 700, color: 'var(--ink-1)', marginBottom: 8 }}>
            Confirm your decision
          </div>
          <p style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.7 }}>
            {isApprove
              ? 'Approving this request will grant the applicant full admin access to PropAtlas, including the ability to manage users, view audit logs, and change platform configuration.'
              : 'Rejecting this request will permanently disable the applicant\'s admin account. They will need to submit a new request if they still want access.'}
          </p>
        </div>

        {state.status === 'error' && (
          <div style={{ padding: 12, background: 'rgba(139,46,46,0.08)', border: '1px solid rgba(139,46,46,0.2)', borderRadius: 4, color: 'var(--crimson)', fontSize: 13, marginBottom: 16 }}>
            {state.message}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/login" className="btn" style={{ textDecoration: 'none' }}>Cancel</Link>
          <button
            type="button"
            className="btn primary"
            disabled={confirming}
            onClick={submit}
            style={isReject ? { background: 'var(--crimson)', borderColor: 'var(--crimson)' } : undefined}
          >
            {confirming
              ? 'Submitting…'
              : (isApprove ? 'Yes, approve' : 'Yes, reject')}
          </button>
        </div>

        <p style={{ marginTop: 20, fontSize: 12, color: 'var(--ink-4)', textAlign: 'center', lineHeight: 1.6 }}>
          Approval links expire 7 days after the request is submitted and can only be used once.
        </p>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--paper)', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 480 }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <img src="/brand/svg/propatlas-mark.svg" className="brand-mark" alt="PropAtlas mark" style={{ width: 34 }} />
            <span className="brand-name" style={{ fontSize: 28 }}>Prop<em>A</em>tlas</span>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}
